// Eskom PARCC — self-service password change / set.
//
// Controlled modal: the parent owns `open` and `onClose`. Two modes, chosen from
// the signed-in user's providers:
//   - has a password credential → CHANGE: set a new password directly, no
//     current-password reauthentication (via `changePassword`).
//   - no password (e.g. a Google-only sign-in) → SET: link an email/password
//     credential so they can also sign in with a password (via `addPassword`).
// On success the modal closes; errors surface inline.
//
// Styling uses the purpose-built `.password-modal-*` classes + `#passwordChangeStatus`
// from the monolith (more on-brand than the generic `.modal-*` classes).
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../lib/auth';

export function ChangePassword({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const { user, changePassword, addPassword } = useAuth();
  // A Google-only sign-in has no password credential: there is nothing to
  // reauthenticate against, so switch to "set a password" (link a credential)
  // rather than "change password" (reauth + update).
  const hasPassword = user?.providerData.some((p) => p.providerId === 'password') ?? false;
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<{ kind: 'ok' | 'bad'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The post-success auto-close runs on a timer; clear it on unmount so it can
  // never fire a setState on a gone component (e.g. the modal closes within the
  // 900ms window). Hooks must run unconditionally, so this sits above the
  // `!open` early return.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) {
        clearTimeout(closeTimer.current);
      }
    };
  }, []);

  if (!open) {
    return null;
  }

  function reset(): void {
    setNewPassword('');
    setConfirmPassword('');
    setStatus(null);
    setSubmitting(false);
  }

  function handleClose(): void {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    reset();
    onClose();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setStatus(null);

    // 6 is Firebase Auth's own floor and matches the existing change-password
    // rule (self-service). Admin-conveyed temp passwords use a higher floor (8).
    if (newPassword.length < 6) {
      setStatus({ kind: 'bad', message: 'The new password must be at least 6 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus({ kind: 'bad', message: 'The new passwords do not match.' });
      return;
    }

    setSubmitting(true);
    try {
      if (hasPassword) {
        await changePassword(newPassword);
      } else {
        await addPassword(newPassword);
      }
      setStatus({ kind: 'ok', message: hasPassword ? 'Password updated.' : 'Password set.' });
      // Close shortly after, giving the user a moment to see confirmation. The
      // id is retained so the cleanup effect / handleClose can cancel it.
      closeTimer.current = setTimeout(handleClose, 900);
    } catch (err) {
      setStatus({
        kind: 'bad',
        message: err instanceof Error ? err.message : 'Unable to set your password.',
      });
      setSubmitting(false);
    }
  }

  // Render through a portal to <body>. The modal must NOT live inside <header>
  // (its real DOM parent), because the header's `backdrop-filter` makes it the
  // containing block for `position: fixed` descendants — which would size this
  // fixed overlay to the header instead of the viewport and clip it off-screen.
  return createPortal(
    <div
      className="password-modal-backdrop open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="changePasswordHeading"
    >
      <div className="password-modal-card">
        <h3 id="changePasswordHeading">{hasPassword ? 'Change password' : 'Set a password'}</h3>
        <p>
          {hasPassword
            ? 'Choose a new password. You will stay signed in after the change.'
            : 'Choose a password so you can also sign in with your email and password. You will stay signed in.'}
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="newPassword">
            New password
            <input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={submitting}
              required
            />
          </label>

          <label htmlFor="confirmPassword">
            Confirm new password
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitting}
              required
            />
          </label>

          {status && (
            <div
              id="passwordChangeStatus"
              className={status.kind}
              role={status.kind === 'bad' ? 'alert' : 'status'}
            >
              {status.message}
            </div>
          )}

          <div className="password-modal-actions">
            <button type="button" className="btn ghost" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting
                ? hasPassword
                  ? 'Updating…'
                  : 'Setting…'
                : hasPassword
                  ? 'Update password'
                  : 'Set password'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
